import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@umanforge/common';
import { ClientService } from 'services/client.service';
import { SiteService } from 'services/site.service';
import { SectorService } from 'services/sector.service';
import 'dayjs/locale/de';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/bg';
import 'dayjs/locale/cs';
import 'dayjs/locale/hu';
import 'dayjs/locale/ro';
import 'dayjs/locale/sk';
import 'dayjs/locale/sv';
import { BaseActionTicketComponent } from './base_action_ticket.component';
import { configService, Client, Site, Sector, LoggerService } from '@umanforge/common';

dayjs.extend( LocalizedFormat );

const logger = new LoggerService( 'PlaniteamActionTicketComponent' );

type LooseSector = Sector & { idSite?: string; id_site?: string; siteId?: string; site_id?: string; site?: string };
type LooseSite = Site & { idClient?: string; id_client?: string; clientId?: string; client_id?: string; client?: string };

interface PlaniteamActionTicketProps {
  id: string;
  missionTitle: string;
  missionIcon: string | null | undefined;
  location: string | null | undefined;
  startDate: string;
  endDate: string;
  hierarchy?: {
    client?: string;
    site?: string;
    sector?: string;
    eventManager?: string;
  };
  eventManagerName?: string;
  clientId?: string;
  siteId?: string;
  sectorId?: string;
}

export function PlaniteamActionTicketComponent( {
  missionTitle,
  missionIcon,
  location,
  startDate,
  endDate,
  hierarchy,
  eventManagerName,
  clientId,
  siteId,
  sectorId,
}: PlaniteamActionTicketProps ) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } ) as string | null;
  const [ iconElement, setIconElement ] = useState<JSX.Element | null>( null );
  const [ displayStartDateTime, setDisplayStartDateTime ] = useState<string>( '' );
  const [ displayEndDateTime, setDisplayEndDateTime ] = useState<string>( '' );
  const [ durationMinutes, setDurationMinutes ] = useState<number>( 0 );
  const [ clientName, setClientName ] = useState<string | null>( null );
  const [ siteName, setSiteName ] = useState<string | null>( null );
  const [ sectorName, setSectorName ] = useState<string | null>( null );

  // Configurer et reformater quand la langue change
  useEffect( () => {
    dayjs.locale( i18n.language );

    const startDateTime = dayjs( startDate );
    const endDateTime = dayjs( endDate );

    // Format date + heure: date locale + heures:minutes
    setDisplayStartDateTime( `${startDateTime.format( 'L' )} ${startDateTime.format( 'HH:mm' )}` );
    setDisplayEndDateTime( `${endDateTime.format( 'L' )} ${endDateTime.format( 'HH:mm' )}` );
    setDurationMinutes( endDateTime.diff( startDateTime, 'minute' ) );
  }, [ i18n.language, startDate, endDate ] );

  useEffect( () => {
    const loadIcon = async () => {
      if ( !missionIcon ) {
        setIconElement( null );
        return;
      }

      // Gérer les URLs avec uploads/ ou http
      if ( missionIcon.includes( 'uploads/' ) || missionIcon.startsWith( 'http' ) ) {
        // Ajouter / au début si c'est un chemin relatif
        const iconSrc = missionIcon.startsWith( '/' ) ? missionIcon : `/${missionIcon}`;
        const backendUrl = await configService.getBackendUrl();
        const fullUrl = backendUrl + iconSrc;
        setIconElement(
          <img
            src={ fullUrl }
            alt={ missionTitle }
            className='w-6 h-6 object-contain'
            onError={ e => {
              e.currentTarget.style.display = 'none';
            } }
          />
        );
      } else {
        setIconElement( <span>{ missionIcon }</span> );
      }
    };

    loadIcon();
  }, [ missionIcon, missionTitle ] );

  useEffect( () => {
    const resolveHierarchy = async () => {


      if ( !token ) { return; }

      try {
        setClientName( null );
        setSiteName( null );
        setSectorName( null );

        let sectorTyped: LooseSector | null = null;
        let siteTyped: LooseSite | null = null;

        if ( sectorId ) {
          const sector = await SectorService.getSector( sectorId, token, dispatch ).catch( () => {return null;} );
          if ( sector ) {
            sectorTyped = sector as unknown as LooseSector;
            setSectorName( sectorTyped.name ?? null );
          }
        }

        const siteIdFromSector = sectorTyped ? (
          sectorTyped.siteId ?? sectorTyped.idSite ?? sectorTyped.site_id ?? sectorTyped.id_site ?? null
        ) : null;
        const siteIdToUse = siteId ?? siteIdFromSector;
        if ( siteIdToUse ) {
          const site = await SiteService.getSite( siteIdToUse, token, dispatch ).catch( () => {return null;} );
          if ( site ) {
            siteTyped = site as unknown as LooseSite;
            setSiteName( siteTyped.name ?? null );
          }
        } else if ( siteId && !siteTyped ) {
          const site = await SiteService.getSite( siteId, token, dispatch ).catch( () => {return null;} );
          if ( site ) {
            siteTyped = site as unknown as LooseSite;
            setSiteName( siteTyped.name ?? null );
          }
        }

        const clientIdFromSite = siteTyped ? (
          siteTyped.clientId ?? siteTyped.idClient ?? siteTyped.client_id ?? siteTyped.id_client ?? null
        ) : null;
        const clientIdToUse = clientId ?? clientIdFromSite;
        if ( clientIdToUse ) {
          const client = await ClientService.getClient( clientIdToUse, token, dispatch ).catch( () => {return null;} );
          if ( client ) { const clientTyped = client as Client; setClientName( clientTyped.name ?? null ); }
        } else if ( clientId ) {
          const client = await ClientService.getClient( clientId, token, dispatch ).catch( () => {return null;} );
          if ( client ) { const clientTyped = client as Client; setClientName( clientTyped.name ?? null ); }
        }
      } catch ( _err ) {
        logger.error( 'Error resolving hierarchy names', _err );
      }
    };

    void resolveHierarchy();
  }, [ hierarchy, clientId, siteId, sectorId, token ] );

  const header = iconElement ? (
    <div className='flex items-center gap-3'>
      <div className='text-teal-600 text-2xl flex-shrink-0'>
        { iconElement }
      </div>
      <h4 className='text-gray-800 font-semibold flex-1 min-w-0 truncate'>
        { missionTitle }
      </h4>
    </div>
  ) : undefined;

  // Footer - hierarchie (client/site/sector/eventManager) à gauche
  const footer = {
    left: (
      <div className='flex gap-4 text-xs text-gray-600'>
        { ( () => {
          const parts: string[] = [];
          const clientToShow = hierarchy?.client ?? clientName;
          const siteToShow = hierarchy?.site ?? siteName;
          const sectorToShow = hierarchy?.sector ?? sectorName;
          const eventManagerToShow = hierarchy?.eventManager ?? eventManagerName ?? null;

          if ( clientToShow ) { parts.push( clientToShow ); }
          if ( siteToShow ) { parts.push( siteToShow ); }
          if ( sectorToShow ) { parts.push( sectorToShow ); }
          if ( eventManagerToShow ) { parts.push( eventManagerToShow ); }

          return parts.length ? (
            <span className='text-xs text-gray-600'>
              { parts.map( ( part, idx ) => { return (
                <span key={ idx } className='inline'>
                  { part }
                  { idx < parts.length - 1 && '/' }
                  { idx === 1 && parts.length > 2 ? <wbr /> : null }
                </span>
              ); } ) }
            </span>
          ) : null;
        } )() }
      </div>
    ),
  };

  return (
    <BaseActionTicketComponent
      borderColor='border-l-4 border-teal-500'
      backgroundColor='bg-blue-100'
      header={ header }
      title=''
      hierarchy={ hierarchy }
      footer={ footer }
    >
      { location && (
        <div className='flex items-center justify-between pb-2 border-b border-teal-200'>
          <span className='text-gray-600 text-xs font-medium'>{ t( 'location' ) }:</span>
          <span className='text-gray-700 text-xs font-semibold'>{ location }</span>
        </div>
      ) }
      <div className='flex items-center justify-between'>
        <span className='text-gray-600'>{ t( 'common:start' ) }:</span>
        <span className='font-medium text-gray-700'>
          { displayStartDateTime }
        </span>
      </div>
      <div className='flex items-center justify-between'>
        <span className='text-gray-600'>{ t( 'common:end' ) }:</span>
        <span className='font-medium text-gray-700'>
          { displayEndDateTime }
        </span>
      </div>
      {
        durationMinutes > 0 &&
          <div className='flex items-center justify-between pt-2 border-t border-teal-200'>
            <span className='text-gray-600 text-xs'>{ t( 'duration' ) }:</span>
            <span className='font-semibold text-teal-700 text-xs'>
              { Math.floor( durationMinutes / 60 ) }h { ( durationMinutes % 60 ).toString().padStart( 2, '0' ) }m
            </span>
          </div>
      }
    </BaseActionTicketComponent>
  );
}
